import {
  createCompanyInput,
  DEFAULT_COUNTRY,
  normalizeCountry,
  normalizePostalCode,
  normalizeRegion,
  createContactInput,
  LEAD_SOURCES,
  RELATIONSHIP_TYPES,
  type CreateCompanyInput,
  type CreateContactInput,
  type RelationshipType,
} from '@buildbee/contracts';

/**
 * CSV import/export rules (RFC 4180). Parsing is pure so the same code runs in the API and in
 * the browser preview.
 */

/** Parse CSV text into rows of cells. Handles quotes, escaped quotes, CRLF/LF/CR and a UTF-8 BOM. */
export function parseCsv(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const delimiter = detectDelimiter(src);
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;
  const n = src.length;
  while (i < n) {
    const ch = src[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      cell += ch;
      i++;
      continue;
    }
    if (ch === '"' && cell === '') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === delimiter) {
      row.push(cell);
      cell = '';
      i++;
      continue;
    }
    if (ch === '\r' || ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
      i += ch === '\r' && src[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    cell += ch;
    i++;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** Comma unless the header line clearly uses semicolons or tabs (Excel in some locales). */
function detectDelimiter(text: string): string {
  const firstLine = text.slice(0, Math.max(0, text.search(/\r|\n/)) || text.length);
  const counts = { ',': 0, ';': 0, '\t': 0 } as Record<string, number>;
  let inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch in counts) counts[ch] = (counts[ch] ?? 0) + 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]!;
  return best[1] > 0 ? best[0] : ',';
}

export type CsvCell = string | number | boolean | null | undefined | Date | readonly string[];

/**
 * Escape one cell. Values that a spreadsheet would execute as a formula (`=`, `+`, `-`, `@`,
 * tab, CR) get a leading apostrophe (CSV injection guard). Plain negative numbers stay numbers.
 */
export function csvCell(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  let s: string;
  if (value instanceof Date) s = value.toISOString();
  else if (Array.isArray(value)) s = value.join('; ');
  else s = String(value);
  if (typeof value === 'string' && /^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Serialize rows to CSV (CRLF line endings, as Excel expects). */
export function toCsv(rows: readonly (readonly CsvCell[])[]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

// ---------------------------------------------------------------------------
// Column mapping
// ---------------------------------------------------------------------------

export type ImportField = { key: string; label: string; aliases: string[] };

export const CONTACT_IMPORT_FIELDS: ImportField[] = [
  { key: 'firstName', label: 'First name', aliases: ['first name', 'first', 'given name', 'firstname', 'fname'] },
  { key: 'lastName', label: 'Last name', aliases: ['last name', 'last', 'surname', 'family name', 'lastname', 'lname'] },
  { key: 'displayName', label: 'Full name', aliases: ['name', 'full name', 'display name', 'contact name', 'contact'] },
  { key: 'email', label: 'Email', aliases: ['email', 'e-mail', 'email address', 'primary email', 'work email'] },
  { key: 'email2', label: 'Other email', aliases: ['email 2', 'secondary email', 'other email', 'personal email'] },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'phone number', 'work phone', 'telephone', 'tel', 'office phone'] },
  { key: 'mobile', label: 'Mobile', aliases: ['mobile', 'cell', 'mobile phone', 'cell phone'] },
  { key: 'company', label: 'Company', aliases: ['company', 'company name', 'organization', 'organisation', 'account', 'employer'] },
  { key: 'jobTitle', label: 'Job title', aliases: ['title', 'job title', 'position', 'role'] },
  { key: 'relationshipTypes', label: 'Relationship type', aliases: ['type', 'relationship', 'relationship type', 'category', 'contact type'] },
  { key: 'source', label: 'Lead source', aliases: ['source', 'lead source', 'referral source'] },
  { key: 'address.line1', label: 'Street', aliases: ['address', 'street', 'address 1', 'address line 1', 'street address'] },
  { key: 'address.line2', label: 'Street line 2', aliases: ['address 2', 'address line 2', 'suite', 'unit'] },
  { key: 'address.city', label: 'City', aliases: ['city', 'town'] },
  { key: 'address.region', label: 'Province/State', aliases: ['province/state', 'state/province', 'province state', 'state province', 'province', 'state', 'prov', 'province or state', 'state or province', 'region', 'territory', 'county'] },
  { key: 'address.postalCode', label: 'Postal/ZIP code', aliases: ['postal/zip code', 'zip/postal code', 'postal zip code', 'zip postal code', 'postal/zip', 'zip/postal', 'postal code/zip', 'postal code', 'postal', 'postcode', 'zip', 'zip code', 'zipcode'] },
  { key: 'address.country', label: 'Country', aliases: ['country', 'country code', 'nation'] },
  { key: 'tags', label: 'Tags', aliases: ['tags', 'labels'] },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'comments', 'description'] },
];

export const COMPANY_IMPORT_FIELDS: ImportField[] = [
  { key: 'name', label: 'Company name', aliases: ['name', 'company', 'company name', 'organization', 'account name', 'business name'] },
  { key: 'relationshipTypes', label: 'Relationship type', aliases: ['type', 'relationship', 'relationship type', 'category'] },
  { key: 'domain', label: 'Domain', aliases: ['domain', 'email domain'] },
  { key: 'website', label: 'Website', aliases: ['website', 'web', 'url', 'web site', 'homepage'] },
  { key: 'email', label: 'Email', aliases: ['email', 'e-mail', 'email address'] },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'phone number', 'telephone', 'main phone'] },
  { key: 'trades', label: 'Trades', aliases: ['trade', 'trades', 'specialty', 'scope'] },
  { key: 'licenseNumber', label: 'License number', aliases: ['license', 'license number', 'licence', 'license #', 'contractor license'] },
  { key: 'insuranceExpiresAt', label: 'Insurance expiry', aliases: ['insurance expiry', 'insurance expires', 'coi expiry', 'gl expiry'] },
  { key: 'workersCompExpiresAt', label: "Workers' comp expiry", aliases: ['workers comp expiry', 'wc expiry', 'workers comp expires'] },
  { key: 'address.line1', label: 'Street', aliases: ['address', 'street', 'address 1', 'address line 1'] },
  { key: 'address.line2', label: 'Street line 2', aliases: ['address 2', 'address line 2', 'suite'] },
  { key: 'address.city', label: 'City', aliases: ['city', 'town'] },
  { key: 'address.region', label: 'Province/State', aliases: ['province/state', 'state/province', 'province state', 'state province', 'province', 'state', 'prov', 'province or state', 'state or province', 'region', 'territory', 'county'] },
  { key: 'address.postalCode', label: 'Postal/ZIP code', aliases: ['postal/zip code', 'zip/postal code', 'postal zip code', 'zip postal code', 'postal/zip', 'zip/postal', 'postal code/zip', 'postal code', 'postal', 'postcode', 'zip', 'zip code', 'zipcode'] },
  { key: 'address.country', label: 'Country', aliases: ['country', 'country code', 'nation'] },
  { key: 'tags', label: 'Tags', aliases: ['tags', 'labels'] },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'comments', 'description'] },
];

export function importFieldsFor(entity: 'contact' | 'company'): ImportField[] {
  return entity === 'contact' ? CONTACT_IMPORT_FIELDS : COMPANY_IMPORT_FIELDS;
}

const headerKey = (h: string) =>
  h
    .trim()
    .toLowerCase()
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ');

/**
 * Suggest a mapping header -> field key. Headers named `custom.<key>` or matching a custom field
 * key/label map to `custom.<key>`. Unknown headers are left out (skipped on import).
 */
export function suggestMapping(
  headers: readonly string[],
  entity: 'contact' | 'company',
  customFields: readonly { key: string; label: string }[] = [],
): Record<string, string> {
  const fields = importFieldsFor(entity);
  const used = new Set<string>();
  const out: Record<string, string> = {};
  for (const h of headers) {
    const k = headerKey(h);
    if (!k) continue;
    let target: string | undefined;
    if (k.startsWith('custom ')) target = `custom.${k.slice(7).replace(/\s+/g, '_')}`;
    target ??= fields.find((f) => f.aliases.some((a) => headerKey(a) === k) || headerKey(f.label) === k || headerKey(f.key) === k)?.key;
    target ??= customFields.find((c) => headerKey(c.key) === k || headerKey(c.label) === k)
      ? `custom.${customFields.find((c) => headerKey(c.key) === k || headerKey(c.label) === k)!.key}`
      : undefined;
    if (target && !used.has(target)) {
      used.add(target);
      out[h] = target;
    }
  }
  return out;
}

const splitList = (v: string) =>
  v
    .split(/[;,|]/)
    .map((s) => s.trim())
    .filter(Boolean);

function parseRelationshipTypes(v: string): RelationshipType[] {
  const out: RelationshipType[] = [];
  for (const raw of splitList(v)) {
    const k = raw.toLowerCase().replace(/[\s/&-]+/g, '_');
    const match =
      (RELATIONSHIP_TYPES as readonly string[]).find((t) => t === k) ??
      (k.startsWith('sub') ? 'subcontractor' : undefined) ??
      (k.includes('vendor') || k.includes('supplier') ? 'supplier' : undefined) ??
      (k.includes('architect') || k.includes('engineer') ? 'architect_engineer' : undefined) ??
      (k.includes('lead') ? 'prospect' : undefined) ??
      (k.includes('customer') ? 'client' : undefined) ??
      (k.includes('employee') || k.includes('staff') ? 'internal' : undefined);
    if (match && !out.includes(match as RelationshipType)) out.push(match as RelationshipType);
  }
  return out;
}

function parseSource(v: string): string | null {
  const k = v.trim().toLowerCase().replace(/[\s/&-]+/g, '_');
  if (!k) return null;
  return (LEAD_SOURCES as readonly string[]).find((s) => s === k) ?? (k.includes('refer') ? 'referral' : k.includes('web') ? 'website' : 'other');
}

/** Accepts YYYY-MM-DD, M/D/YYYY, M/D/YY. Returns null when unparseable. */
export function parseDateCell(v: string): string | null {
  const s = v.trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s);
  if (!m) return null;
  const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  const month = Number(m[1]);
  const day = Number(m[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export type MappedRow<T> =
  | { ok: true; value: T; companyName: string | null }
  | { ok: false; errors: string[]; companyName: string | null };

/** Turn one CSV row into a raw object keyed by the mapped field paths. */
function collect(headers: readonly string[], row: readonly string[], mapping: Record<string, string>): Map<string, string> {
  const values = new Map<string, string>();
  headers.forEach((h, i) => {
    const target = mapping[h];
    const v = (row[i] ?? '').trim();
    if (!target || !v) return;
    values.set(target, values.has(target) ? `${values.get(target)}; ${v}` : v);
  });
  return values;
}

/**
 * Address from the mapped cells. The country may be a code or a name ("Canada", "USA"); when it is
 * missing it is inferred from the province/state or postal code (Canada first), else Canada. The
 * province/state and postal code are normalized by `addressSchema` when the row is validated.
 */
export function buildAddress(values: Map<string, string>) {
  const parts = ['line1', 'line2', 'city', 'region', 'postalCode', 'country'] as const;
  if (!parts.some((p) => values.has(`address.${p}`))) return undefined;
  const address: Record<string, string> = {};
  for (const p of parts) {
    const v = values.get(`address.${p}`);
    if (v !== undefined) address[p] = v;
  }
  if (address.country !== undefined) address.country = normalizeCountry(address.country) ?? address.country;
  else address.country = inferCountry(address.region, address.postalCode);
  return address;
}

function inferCountry(region: string | undefined, postalCode: string | undefined): string {
  for (const c of ['CA', 'US'] as const) {
    if (region && normalizeRegion(c, region)) return c;
  }
  for (const c of ['CA', 'US'] as const) {
    if (postalCode && normalizePostalCode(c, postalCode)) return c;
  }
  return DEFAULT_COUNTRY;
}

function buildCustom(values: Map<string, string>): Record<string, string> {
  const custom: Record<string, string> = {};
  for (const [k, v] of values) if (k.startsWith('custom.')) custom[k.slice(7)] = v;
  return custom;
}

function zodErrors(error: { issues: { path: PropertyKey[]; message: string }[] }): string[] {
  return error.issues.map((i) => (i.path.length ? `${i.path.map(String).join('.')}: ${i.message}` : i.message));
}

export function mapContactRow(
  headers: readonly string[],
  row: readonly string[],
  mapping: Record<string, string>,
  defaults: { relationshipType: RelationshipType },
): MappedRow<CreateContactInput> {
  const v = collect(headers, row, mapping);
  let firstName = v.get('firstName') ?? '';
  let lastName = v.get('lastName') ?? '';
  const full = v.get('displayName');
  if (full && !firstName && !lastName) {
    const parts = full.split(/\s+/);
    firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : full;
    lastName = parts.length > 1 ? parts[parts.length - 1]! : '';
  }
  const emails = [v.get('email'), v.get('email2')]
    .flatMap((e) => (e ? splitList(e) : []))
    .map((email, i) => ({ email, label: i === 0 ? 'work' : 'other', primary: i === 0 }));
  const phones = [
    ...(v.get('phone') ? [{ phone: v.get('phone')!, label: 'work' }] : []),
    ...(v.get('mobile') ? [{ phone: v.get('mobile')!, label: 'mobile' }] : []),
  ].map((p, i) => ({ ...p, primary: i === 0 }));
  const types = v.get('relationshipTypes') ? parseRelationshipTypes(v.get('relationshipTypes')!) : [];
  const companyName = v.get('company') ?? null;
  const raw = {
    firstName,
    lastName,
    displayName: full || undefined,
    jobTitle: v.get('jobTitle') ?? null,
    relationshipTypes: types.length ? types : [defaults.relationshipType],
    emails,
    phones,
    address: buildAddress(v),
    source: v.get('source') ? parseSource(v.get('source')!) : null,
    tags: v.get('tags') ? splitList(v.get('tags')!) : [],
    notes: v.get('notes') ?? null,
    custom: buildCustom(v),
  };
  if (!firstName && !lastName && !full && emails.length === 0 && !companyName) {
    return { ok: false, errors: ['Row has no name, email or company'], companyName };
  }
  const parsed = createContactInput.safeParse(raw);
  return parsed.success ? { ok: true, value: parsed.data, companyName } : { ok: false, errors: zodErrors(parsed.error), companyName };
}

export function mapCompanyRow(
  headers: readonly string[],
  row: readonly string[],
  mapping: Record<string, string>,
  defaults: { relationshipType: RelationshipType },
): MappedRow<CreateCompanyInput> {
  const v = collect(headers, row, mapping);
  const types = v.get('relationshipTypes') ? parseRelationshipTypes(v.get('relationshipTypes')!) : [];
  const errors: string[] = [];
  const dateField = (key: string) => {
    const raw = v.get(key);
    if (!raw) return null;
    const d = parseDateCell(raw);
    if (!d) errors.push(`${key}: unrecognized date "${raw}"`);
    return d;
  };
  let website = v.get('website') ?? null;
  if (website && !/^https?:\/\//i.test(website)) website = `https://${website}`;
  const raw = {
    name: v.get('name') ?? '',
    relationshipTypes: types.length ? types : [defaults.relationshipType],
    domain: v.get('domain') ?? null,
    website,
    email: v.get('email') ?? null,
    phone: v.get('phone') ?? null,
    trades: v.get('trades') ? splitList(v.get('trades')!) : [],
    licenseNumber: v.get('licenseNumber') ?? null,
    insuranceExpiresAt: dateField('insuranceExpiresAt'),
    workersCompExpiresAt: dateField('workersCompExpiresAt'),
    address: buildAddress(v),
    tags: v.get('tags') ? splitList(v.get('tags')!) : [],
    notes: v.get('notes') ?? null,
    custom: buildCustom(v),
  };
  const parsed = createCompanyInput.safeParse(raw);
  if (!parsed.success) return { ok: false, errors: [...errors, ...zodErrors(parsed.error)], companyName: null };
  if (errors.length) return { ok: false, errors, companyName: null };
  return { ok: true, value: parsed.data, companyName: null };
}
